import { createContext, useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { SheetSourceType } from '../../entities';

export interface GuitarSettings {
  style?: 'finger_picking' | 'strumming';
  tuning?:
    | 'standard'
    | 'drop-d'
    | 'open-d'
    | 'open-e'
    | 'open-g'
    | 'open-a'
    | 'dadgad'
    | 'dgcgcd'
    | 'half-step-down'
    | 'full-step-down'
    | 'nashville';
  capo?: number;
}

export interface BassSettings {
  tuning?: 'standard' | 'drop-d' | '5-string-low' | '5-string-high' | '6-string';
}

export interface PianoSettings {
  pedal?: boolean;
  style?: 'classic' | 'pop';
  hand_separation?: 'None' | 'split=60' | 'single';
  clef_changes?: boolean;
}

export interface DrumSettings {
  drum_notes: 'all' | 'standard' | 'simple';
}

export interface VocalSettings {
  voice_type?: 'soprano' | 'alto' | 'tenor' | 'bass';
  lyrics?: boolean;
  lang?: string;
}

export type InstrumentType =
  | 'vocals'
  | 'piano'
  | 'pitched_percussion'
  | 'organ'
  | 'acoustic_guitar'
  | 'electric_guitar'
  | 'bass'
  | 'strings'
  | 'brass'
  | 'reed'
  | 'pipe'
  | 'synth_lead'
  | 'synth_pad'
  | 'drums'
  | 'chords'
  | 'synth'
  | 'guitar'
  | 'wind'
  | 'alto_sax'
  | 'tenor_sax'
  | 'soprano_sax'
  | 'baritone_sax'
  | 'clarinet'
  | 'flute'
  | 'oboe'
  | 'bassoon'
  | 'trumpet'
  | 'trombone'
  | 'tuba'
  | 'recorder'
  | 'french_horn'
  | 'violin'
  | 'viola'
  | 'cello'
  | 'double_bass';

export type InstrumentSettings = GuitarSettings | PianoSettings | DrumSettings | VocalSettings;

export interface TranscriptionDialogContextValue {
  videoUrl?: string;
  videoId?: string;
  file?: File;
  source: SheetSourceType;
  settings: {
    instruments: InstrumentType[];
    solo: boolean;
    key?: string;
    time_signature?: string;
    tempo?: string;
    triplets: boolean;
    min_duration: number;
    exact_tempo?: number;
    vocals?: VocalSettings;
    piano?: PianoSettings;
    acoustic_guitar?: GuitarSettings;
    electric_guitar?: GuitarSettings;
    drums?: DrumSettings;
    bass?: BassSettings;
    start_time?: number;
    stop_time?: number;
    audio_duration?: number;
    arrangement?: boolean;
    difficulty?: 'simple' | 'full';
    model?: 'universal' | 'rock' | 'classic';
  };
  apriori: {
    title: string;
    composer: string;
    agreedToTerms: boolean;
  };
  setSettings: (
    valueMap: Partial<TranscriptionDialogContextValue['settings']> | Partial<InstrumentSettings>,
    instrument?: InstrumentType
  ) => void;
  setApriori: (valueMap: Partial<TranscriptionDialogContextValue['apriori']>) => void;
  setFile: (file: File) => void;
  setVideoUrl: (videoUrl: string) => void;
  setSource: (source: SheetSourceType) => void;
  reset: () => void;
}

export const TranscriptionDialogContext = createContext<TranscriptionDialogContextValue>({
  videoUrl: null,
  videoId: null,
  file: null,
  source: null,
  settings: {
    instruments: [],
    solo: false,
    min_duration: 0.0625,
    triplets: true,
    arrangement: undefined,
    difficulty: undefined,
  },
  apriori: {
    title: '',
    composer: '',
    agreedToTerms: false,
  },
  setSettings: () => {},
  setApriori: () => {},
  setFile: () => {},
  setVideoUrl: () => {},
  setSource: () => {},
  reset: () => {},
});

const initialSettings: TranscriptionDialogContextValue['settings'] = {
  instruments: [],
  solo: false,
  min_duration: 0.0625,
  triplets: true,
};

export const TranscriptionDialogContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] =
    useState<TranscriptionDialogContextValue['settings']>(initialSettings);
  const [apriori, setApriori] = useState<TranscriptionDialogContextValue['apriori']>({
    title: '',
    composer: '',
    agreedToTerms: false,
  });

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [file, setFile] = useState<File | undefined>();
  const [source, setSource] = useState<SheetSourceType | null>(SheetSourceType.File);

  const handleSetSettings = useCallback(
    (
      valueMap: Partial<TranscriptionDialogContextValue['settings']> | Partial<InstrumentSettings>,
      instrument?: InstrumentType
    ) => {
      const filteredMap = { ...valueMap };
      Object.keys(filteredMap).forEach((key: keyof TranscriptionDialogContextValue['settings']) => {
        // @ts-ignore
        if (filteredMap && filteredMap[key] === 'null') {
          // @ts-ignore
          filteredMap[key] = null;
        }
      });
      if (instrument) {
        setSettings((prevFormData) => ({
          ...prevFormData,
          [instrument]: {
            ...(prevFormData[instrument as keyof typeof prevFormData] as InstrumentSettings),
            ...filteredMap,
          },
        }));
      } else {
        setSettings((prevFormData) => ({ ...prevFormData, ...filteredMap }));
      }
    },
    [setSettings]
  );

  const handleSetApriori = useCallback(
    (valueMap: Partial<TranscriptionDialogContextValue['apriori']>) => {
      setApriori((prevFormData) => ({
        ...prevFormData,
        ...valueMap,
      }));
    },
    [setApriori]
  );

  const reset = useCallback(() => {
    setSettings(initialSettings);
    setApriori({
      title: '',
      composer: '',
      agreedToTerms: false,
    });
    setVideoUrl(null);
    setFile(null);
    setSource(null);
  }, [setSettings, setApriori, setVideoUrl, setFile]);

  const contextValue: TranscriptionDialogContextValue = useMemo(
    () => ({
      settings,
      apriori,
      source,
      setApriori: handleSetApriori,
      setSettings: handleSetSettings,
      videoUrl,
      file,
      setFile,
      setVideoUrl,
      setSource,
      reset,
    }),
    [
      settings,
      apriori,
      source,
      handleSetApriori,
      handleSetSettings,
      videoUrl,
      file,
      reset,
      videoUrl,
      setVideoUrl,
    ]
  );

  return (
    <TranscriptionDialogContext.Provider value={contextValue}>
      {children}
    </TranscriptionDialogContext.Provider>
  );
};
